import React from 'react';
import { AlertTriangle, ClipboardCheck, Clock } from 'lucide-react';
import type { ReportsSummary } from '@/types';
import { getReportsSummary } from '@/services/api';
import { toast } from 'react-hot-toast';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

function defaultRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const toInput = (d: Date) => d.toISOString().slice(0, 10);
  return { start: toInput(start), end: toInput(end) };
}

export function InspectionReports() {
  const [{ start, end }, setRange] = React.useState(defaultRange);
  const [summary, setSummary] = React.useState<ReportsSummary | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getReportsSummary(`${start}T00:00:00.000Z`, `${end}T23:59:59.999Z`)
      .then(data => { if (!cancelled) setSummary(data); })
      .catch(() => { if (!cancelled) toast.error('Failed to load report summary.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [start, end]);

  return (
    <div>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="text-xs text-gray-500 dark:text-gray-400">
          Start
          <input
            type="date"
            value={start}
            onChange={(e) => setRange(r => ({ ...r, start: e.target.value }))}
            className="input-field mt-1 block py-1.5 text-sm"
          />
        </label>
        <label className="text-xs text-gray-500 dark:text-gray-400">
          End
          <input
            type="date"
            value={end}
            onChange={(e) => setRange(r => ({ ...r, end: e.target.value }))}
            className="input-field mt-1 block py-1.5 text-sm"
          />
        </label>
      </div>

      {loading && <LoadingSpinner message="Loading report..." />}

      {!loading && summary && (
        <div className="mt-5 flex flex-col gap-6">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <StatTile icon={<ClipboardCheck />} label="Submissions" value={summary.totalSubmitted} />
            <StatTile icon={<Clock />} label="Missed Vehicle-Days" value={summary.totalMissedVehicleDays} color="text-status-warning" />
            <StatTile icon={<AlertTriangle />} label="Checks Tracked" value={summary.byCheck.length} />
          </div>
          <p className="-mt-3 text-xs text-gray-400">
            Submissions counts only inspections submitted within the selected date range above — it will not match the Dashboard's all-time inspection count.
          </p>

          <section>
            <h3 className="mb-2 text-sm font-semibold text-wire3-navy dark:text-wire3-green">
              Missed Inspections
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 text-left">
                    <th className="pb-2 font-semibold text-gray-500 dark:text-gray-400">Vehicle</th>
                    <th className="pb-2 font-semibold text-gray-500 dark:text-gray-400">Missed Date</th>
                    <th className="pb-2 font-semibold text-gray-500 dark:text-gray-400">Type</th>
                    <th className="pb-2 font-semibold text-gray-500 dark:text-gray-400">Days Overdue</th>
                    <th className="pb-2 font-semibold text-gray-500 dark:text-gray-400">Last Submitted By</th>
                    <th className="pb-2 font-semibold text-gray-500 dark:text-gray-400">Last Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.missedRows.map((row, idx) => (
                    <tr key={`${row.vehicleUnitNumber}-${row.missedDate}-${row.type}-${idx}`} className="border-b border-gray-100 dark:border-gray-700">
                      <td className="py-2 font-medium text-gray-700 dark:text-gray-300">{row.vehicleUnitNumber}</td>
                      <td className="py-2 text-gray-600 dark:text-gray-400">{row.missedDate}</td>
                      <td className="py-2 text-gray-600 dark:text-gray-400 capitalize">{row.type}</td>
                      <td className="py-2 text-status-warning font-semibold">{row.daysOverdue}</td>
                      <td className="py-2 text-gray-600 dark:text-gray-400">{row.lastSubmittedBy || '—'}</td>
                      <td className="py-2 text-gray-600 dark:text-gray-400">{row.lastSubmittedDate || '—'}</td>
                    </tr>
                  ))}
                  {summary.missedRows.length === 0 && (
                    <tr><td colSpan={6} className="py-6 text-center text-gray-400">No missed inspections in this range.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h3 className="mb-2 text-sm font-semibold text-wire3-navy dark:text-wire3-green">
              Submissions & Fails by Inspector
            </h3>
            <p className="mb-3 text-xs text-gray-400">
              "Missed" is attributed to whichever inspector most often submits for that vehicle — there's no formal vehicle assignment, so use it as a lead, not a verdict. See the Missed Inspections table above for exact vehicle/date gaps.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 text-left">
                    <th className="pb-2 font-semibold text-gray-500 dark:text-gray-400">Inspector</th>
                    <th className="pb-2 font-semibold text-gray-500 dark:text-gray-400">Submitted</th>
                    <th className="pb-2 font-semibold text-gray-500 dark:text-gray-400">Fails</th>
                    <th className="pb-2 font-semibold text-gray-500 dark:text-gray-400">Likely Missed</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.byInspector.map(row => (
                    <tr key={row.inspectorName} className="border-b border-gray-100 dark:border-gray-700">
                      <td className="py-2 font-medium text-gray-700 dark:text-gray-300">{row.inspectorName}</td>
                      <td className="py-2 text-gray-600 dark:text-gray-400">{row.submittedCount}</td>
                      <td className="py-2">
                        {row.failCount > 0
                          ? <span className="font-semibold text-status-fail">{row.failCount}</span>
                          : <span className="text-gray-300 dark:text-gray-600">0</span>}
                      </td>
                      <td className="py-2">
                        {row.missedDays > 0
                          ? <span className="font-semibold text-status-warning">{row.missedDays}</span>
                          : <span className="text-gray-300 dark:text-gray-600">0</span>}
                      </td>
                    </tr>
                  ))}
                  {summary.byInspector.length === 0 && (
                    <tr><td colSpan={4} className="py-6 text-center text-gray-400">No submissions in this range.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h3 className="mb-3 text-sm font-semibold text-wire3-navy dark:text-wire3-green">
              Fail Rate by Check
            </h3>
            <div className="flex flex-col gap-2">
              {summary.byCheck.filter(c => c.failCount > 0).map(check => (
                <div key={check.checkId} className="flex items-center gap-3">
                  <span className="w-56 shrink-0 truncate text-xs text-gray-600 dark:text-gray-400" title={check.label}>
                    {check.label}
                  </span>
                  <div className="h-2 flex-1 rounded-full bg-gray-100 dark:bg-gray-700">
                    <div
                      className="h-2 rounded-full bg-status-fail"
                      style={{ width: `${Math.max(check.failRate, 2)}%` }}
                    />
                  </div>
                  <span className="w-24 shrink-0 text-right text-xs font-semibold text-gray-600 dark:text-gray-400">
                    {check.failRate}% ({check.failCount}/{check.applicableCount})
                  </span>
                </div>
              ))}
              {summary.byCheck.filter(c => c.failCount > 0).length === 0 && (
                <p className="py-4 text-center text-sm text-gray-400">No failed checks in this range.</p>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function StatTile({ icon, label, value, color }: {
  icon: React.ReactNode; label: string; value: string | number; color?: string;
}) {
  return (
    <div className="card dark:bg-gray-750 dark:border-gray-700 flex flex-col items-center text-center">
      <div className={`h-5 w-5 ${color || 'text-gray-400'}`}>{icon}</div>
      <p className={`mt-2 text-2xl font-bold ${color || 'text-wire3-navy dark:text-gray-100'}`}>{value}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    </div>
  );
}
